import React from 'react';
import { 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Paper
} from '@mui/material';
import { formatCurrency, formatPercentage } from '../domain/copyHelpers';

const ThresholdBadge = ({ badge }) => {
  const getBadgeColor = (badge) => {
    switch (badge.toLowerCase()) {
      case 'higher rate':
        return 'warning';
      case 'additional rate':
        return 'error';
      case 'student loan':
        return 'info';
      case 'personal allowance':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Chip 
      label={badge} 
      size="small" 
      color={getBadgeColor(badge)}
      role="status"
      className="threshold-badge"
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  );
};

export const ComparisonTable = ({ 
  currentSalary, 
  comparisonData = [], 
  loading = false 
}) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Calculating comparison data...</Typography>
        </Box>
      </Paper>
    );
  }

  if (comparisonData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography color="textSecondary" sx={{ textAlign: 'center' }}>
          No comparison data available. Enter a salary to see raise comparisons.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Salary Raise Comparison (1-10%)
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Compare the after-tax impact of different raise percentages for £{currentSalary?.toLocaleString()} salary.
      </Typography>

      <TableContainer 
        sx={{ 
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: 650
          }
        }}
      >
        <Table 
          aria-label="Salary raise comparison table"
          size="small"
        >
          <TableHead>
            <TableRow sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper' }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '80px' }}>
                Raise %
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }} align="right">
                Gross Increase
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }} align="right">
                Net Increase (Annual)
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }} align="right">
                Net Increase (Monthly)
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '100px' }} align="right">
                Effective %
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 'bold', minWidth: '100px' }} 
                align="right"
                title="Amount kept per pound raised after tax"
              >
                Keep per £1
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }}>
                Thresholds
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisonData.map((row) => (
              <TableRow 
                key={row.raisePct}
                sx={{ 
                  '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <TableCell data-testid="raise-pct">
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatPercentage(row.raisePct)}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatCurrency(row.grossDelta)}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatCurrency(row.netDeltaAnnual)}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatCurrency(row.netDeltaMonthly)}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography variant="body2" color="primary.main">
                    {formatPercentage(row.effectiveNetPct)}
                  </Typography>
                </TableCell>
                
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                  >
                    {Math.round(row.keepPerPound * 100)}p
                  </Typography>
                </TableCell>
                
                <TableCell data-testid="threshold-badges">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {row.thresholdBadges?.map((badge, index) => (
                      <ThresholdBadge key={index} badge={badge} />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
        * Threshold badges indicate when a raise crosses significant tax boundaries that affect your marginal rate.
      </Typography>
    </Paper>
  );
};